import { EtherFiWithdrawRequestAbi } from '../abis/etherfi'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, toNumber, type LstWithdrawalReader } from './shared'

/**
 * EtherFi WithdrawRequestNFT — ERC-721 enumerable.
 *
 * Three stages:
 *  1. `balanceOf(user)` → count.
 *  2. `tokenOfOwnerByIndex(user, i)` for i in [0, count) — enumerate
 *     token IDs.
 *  3. `getRequest(id) + isFinalized(id) + isValid(id)` per id — get
 *     per-request status.
 */
export const readerEtherFi: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    // Stage 1.
    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'balanceOf',
          params: [user],
        },
      ] as unknown as never[],
      abi: [EtherFiWithdrawRequestAbi] as unknown as never,
    })) as unknown[]
    const count = toNumber(stage1[0]) ?? 0
    if (count === 0) return []

    // Stage 2.
    const enumCalls = Array.from({ length: count }, (_, i) => ({
      address: entry.withdrawalContract!,
      name: 'tokenOfOwnerByIndex',
      params: [user, BigInt(i)],
    }))
    const stage2 = (await multicallRetry({
      chain: chainId,
      calls: enumCalls as unknown as never[],
      abi: enumCalls.map(() => EtherFiWithdrawRequestAbi) as unknown as never,
    })) as unknown[]
    const ids = stage2.map(toBigInt).filter((v): v is bigint => v !== undefined)
    if (ids.length === 0) return []

    // Stage 3 — fetch detail for each (3 calls per id: getRequest +
    // isFinalized + isValid).
    const detailCalls = ids.flatMap((id) => [
      { address: entry.withdrawalContract!, name: 'getRequest', params: [id] },
      { address: entry.withdrawalContract!, name: 'isFinalized', params: [id] },
      { address: entry.withdrawalContract!, name: 'isValid', params: [id] },
    ])
    const stage3 = (await multicallRetry({
      chain: chainId,
      calls: detailCalls as unknown as never[],
      abi: detailCalls.map(() => EtherFiWithdrawRequestAbi) as unknown as never,
    })) as unknown[]

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i]
      const req = stage3[i * 3] as Record<string, unknown> | undefined
      const isFinalized = Boolean(stage3[i * 3 + 1])
      const isValid = Boolean(stage3[i * 3 + 2])
      if (!req) continue
      if (!isValid) continue // cancelled by admin
      const amount = toBigInt(req.amountOfEEth)
      if (amount === undefined) continue
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: id.toString(),
        amountUnderlying: amount.toString(),
        status: isFinalized ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
